import {
  CCNewAPIDtoAgreementDto,
  CCNewAPIDtoCreateAgreementDto,
  RightioGoCardlessAPIWrapperModelsDtoCustomerRequestDto,
} from "../../apis/ccm/model";
import { api, state, ui } from "../../../interfaces/interfaces";
import {
  dispatchSetSnackMsgError,
  dispatchSetSnackMsgWarn,
  dispatchUILoaded,
  dispatchUILoading,
} from "@reducers/uiReducer";

import AddressUtils from "../../address";
import ObjectsOperations from "../operations";
import { OfferParams_v3 } from "./offerParams";
import { ServiceAgreementAddress } from "./serviceAgreementAddress";
import { ServiceAgreementBody } from "./serviceAgreementBody";
import { ServiceAgreementCustomer } from "./serviceAgreementCustomer";
import { ServiceAgreementPaymentDetails_v2 } from "./serviceAgreementPaymentDetails";
import { TemplateUIObject } from "@services/storage/storeTemplate/index";
import { Version } from "../../version/index";
import { apiV2 } from "@services/CCMAccess/apiAccess";
import { createGoCardlessBankAccount } from "./directDebit";
import { exportObject } from "../../general/index";
import { logger } from "@services/workers/helpers";

export * from "./serviceAgreementBody";
export * from "./serviceAgreementPaymentDetails";
export * from "./offerParams";
export * from "./serviceAgreementAddress";
export * from "./serviceAgreementCustomer";
export * from "./directDebit";

const namespace = "SERVICE AGREEMENT CLASS";

export class ServiceAgreement_v2 {
  constructor() {
    this._serviceAgreement = new ServiceAgreementBody();
    this._serviceAgreementBillingAddress = new ServiceAgreementAddress();
    this._serviceAgreementCustomerAddress = new ServiceAgreementAddress();
    this._serviceAgreementPolicyAddress = new ServiceAgreementAddress();
    this._serviceAgreementPaymentDetails =
      new ServiceAgreementPaymentDetails_v2();
    this._serviceAgreementCustomer = new ServiceAgreementCustomer();
    this._serviceAgreementOffer = new OfferParams_v3();
    this.delayedWritesTimeOut = null;

    // Initialize debounced submit method (300ms delay)
    // this.submitDebounced = this.debounce(this.submit.bind(this), 300);
    // Initialize throttled submit method with a 1 second window
    this.submitThrottled = this.throttle(this.submit.bind(this), 1000);
  }
  private _serviceAgreement: ServiceAgreementBody;
  private _serviceAgreementBillingAddress: ServiceAgreementAddress;
  private _serviceAgreementCustomerAddress: ServiceAgreementAddress;
  private _serviceAgreementPolicyAddress: ServiceAgreementAddress;
  private _serviceAgreementCustomer: ServiceAgreementCustomer;
  private _serviceAgreementPaymentDetails: ServiceAgreementPaymentDetails_v2;
  private _serviceAgreementOffer: OfferParams_v3;

  // New properties for double-submit prevention
  private _isSubmitting: boolean = false;
  private _currentSubmissionKey: string | null = null;

  public get serviceAgreementOffer(): OfferParams_v3 {
    return this._serviceAgreementOffer;
  }

  public set serviceAgreementOffer(serviceAgreementOffer: OfferParams_v3) {
    this._serviceAgreementOffer = serviceAgreementOffer;
  }

  public get serviceAgreement(): ServiceAgreementBody {
    return this._serviceAgreement;
  }

  public set serviceAgreement(serviceAgreement: ServiceAgreementBody) {
    this._serviceAgreement = serviceAgreement;
  }

  public get serviceAgreementBillingAddress(): ServiceAgreementAddress {
    return this._serviceAgreementBillingAddress;
  }

  public set serviceAgreementBillingAddress(
    serviceAgreementBillingAddress: ServiceAgreementAddress,
  ) {
    this._serviceAgreementBillingAddress = serviceAgreementBillingAddress;
  }

  public get serviceAgreementCustomerAddress(): ServiceAgreementAddress {
    return this._serviceAgreementCustomerAddress;
  }

  public set serviceAgreementCustomerAddress(
    serviceAgreementCustomerAddress: ServiceAgreementAddress,
  ) {
    this._serviceAgreementCustomerAddress = serviceAgreementCustomerAddress;
  }

  public get serviceAgreementPolicyAddress(): ServiceAgreementAddress {
    return this._serviceAgreementPolicyAddress;
  }

  public set serviceAgreementPolicyAddress(
    serviceAgreementPolicyAddress: ServiceAgreementAddress,
  ) {
    this._serviceAgreementPolicyAddress = serviceAgreementPolicyAddress;
  }

  public get serviceAgreementCustomer(): ServiceAgreementCustomer {
    return this._serviceAgreementCustomer;
  }

  public set serviceAgreementCustomer(
    serviceAgreementCustomer: ServiceAgreementCustomer,
  ) {
    this._serviceAgreementCustomer = serviceAgreementCustomer;
  }

  public get serviceAgreementPaymentDetails(): ServiceAgreementPaymentDetails_v2 {
    return this._serviceAgreementPaymentDetails;
  }

  public set serviceAgreementPaymentDetails(
    serviceAgreementPaymentDetails: ServiceAgreementPaymentDetails_v2,
  ) {
    this._serviceAgreementPaymentDetails = serviceAgreementPaymentDetails;
  }

  private delayedWritesTimeOut: number | null;

  // Debounced version of the submit method (to ignore rapid clicks)
  // public submitDebounced: (ui: TemplateUIObject, userId: number, ddPayment?: boolean) => void;
  public submitThrottled: (
    ui: TemplateUIObject,
    userId: number,
    ddPayment?: boolean,
  ) => void;
  // Getter for submission state (if needed for UI binding)
  public get isSubmitting(): boolean {
    return this._isSubmitting;
  }
  /**
   * Simple debounce helper to delay execution of a function.
   */
  private debounce(func: Function, wait: number) {
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => {
        func.apply(this, args);
        timeout = null;
      }, wait);
    };
  }

  /**
   * A throttle helper to ensure a function is called at most once within the specified limit.
   */
  private throttle(func: Function, limit: number) {
    let lastRan: number | null = null;
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (lastRan === null) {
        func.apply(this, args);
        lastRan = Date.now();
      } else {
        clearTimeout(timeout!);
        timeout = window.setTimeout(
          () => {
            if (Date.now() - lastRan! >= limit) {
              func.apply(this, args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan),
        );
      }
    };
  }

  /**
   * Generates a unique idempotency key for this submission.
   */
  private generateIdempotencyKey(): string {
    return (
      Date.now().toString() + "-" + Math.random().toString(36).substr(2, 9)
    );
  }

  clear() {
    const temp: any = {};
    temp.serviceAgreement = new ServiceAgreementBody();
    temp.serviceAgreementBillingAddress = new ServiceAgreementAddress();
    temp.serviceAgreementCustomerAddress = new ServiceAgreementAddress();
    temp.serviceAgreementPolicyAddress = new ServiceAgreementAddress();
    temp.serviceAgreementPaymentDetails =
      new ServiceAgreementPaymentDetails_v2();
    temp.serviceAgreementCustomer = new ServiceAgreementCustomer();
    temp.serviceAgreementOffer = new OfferParams_v3();
    this.fillFromStorage(ObjectsOperations.typeSafeStructuredClone(temp));
  }

  reset() {
    this.serviceAgreement = new ServiceAgreementBody();
    this.serviceAgreementBillingAddress = new ServiceAgreementAddress();
    this.serviceAgreementCustomerAddress = new ServiceAgreementAddress();
    this.serviceAgreementPolicyAddress = new ServiceAgreementAddress();
    this.serviceAgreementPaymentDetails =
      new ServiceAgreementPaymentDetails_v2();
    this.serviceAgreementCustomer = new ServiceAgreementCustomer();
    this.serviceAgreementOffer = new OfferParams_v3();
  }

  toRequest(userId: number): CCNewAPIDtoAgreementDto {
    return {
      boilerHeatingTypeId: this.serviceAgreement.boilerHeatingTypeId,
      boilerManufacturerId: this.serviceAgreement.boilerManufacturerId,
      boilerModel: this.serviceAgreement.boilerModel,
      dateBoilerInstalled:
        this.serviceAgreement.dateBoilerInstalled?.toISOString(),
      dateBoilerLastServiced:
        this.serviceAgreement.dateBoilerLastServiced?.toISOString(),
      dateFirstPayment:
        this.serviceAgreement.dateFirstPayment?.toISOString() ?? "",
      dateGasSafetyDue: this.serviceAgreement.dateGasSafetyDue?.toISOString(),
      dateStart: this.serviceAgreement.dateStart?.toISOString() ?? "",
      exclusionDetails: this.serviceAgreement.exclusionDetails,
      feeAnnual: this.serviceAgreement.feeAnnual,
      feeOneOff: this.serviceAgreement.feeOneOff,
      jobId: this.serviceAgreement.jobId,
      notes: this.serviceAgreement.notes,
      referrerCode: this.serviceAgreement.referrerCode,
      serviceAgreementCustomerId:
        this.serviceAgreementCustomer.serviceAgreementCustomerId,
      serviceAgreementId: this.serviceAgreement.serviceAgreementId,
      serviceAgreementIncentiveId:
        this.serviceAgreement.serviceAgreementIncentiveId,
      serviceAgreementReference:
        this.serviceAgreement.serviceAgreementReference,
      serviceAgreementTypeId: this.serviceAgreement.serviceAgreementTypeId,
      serviceAgreementBillingFrequencyId:
        this.serviceAgreementPaymentDetails.serviceAgreementBillingFrequencyId >
        3
          ? this.serviceAgreementOffer.serviceAgreementDurationId
          : this.serviceAgreementPaymentDetails
              .serviceAgreementBillingFrequencyId,
      serviceAgreementDurationId:
        this.serviceAgreementOffer.serviceAgreementDurationId,
      serviceAgreementExcessId:
        ui.CarePlanPlanDetailsPlanOptionsExcess?.serviceAgreementExcessId ?? 0, // FIXME: this.serviceAgreementOffer.serviceAgreementExcessId,
      previousServiceAgreementId: 0,
      userIdEntered: userId,
      userIdUpdated: userId,
      source: Version.appName(),
    };
  }

  private fillFromStorage = (object: ServiceAgreement_v2) => {
    for (const key in object) {
      Object.assign(
        this[key as keyof ServiceAgreement_v2],
        object[key as keyof ServiceAgreement_v2],
      );
    }
  };

  updatePersonalDetails(
    ui: TemplateUIObject,
    scope: "Customer" | "PolicyHolder" | "Billing",
    type: "Address" | "Contact",
  ): void {
    switch (type) {
      case "Address":
        switch (scope) {
          case "Billing":
            this.serviceAgreementBillingAddress.property =
              ui.PropertyAddressProperty;
            this.serviceAgreementBillingAddress.organisation =
              ui.PropertyAddressBusinessName;
            this.serviceAgreementBillingAddress.street =
              ui.PropertyAddressStreet;
            this.serviceAgreementBillingAddress.locality =
              ui.PropertyAddressLocality;
            this.serviceAgreementBillingAddress.town = ui.PropertyAddressCity;
            this.serviceAgreementBillingAddress.postcode =
              ui.PropertyAddressPostcode;
            break;

          case "PolicyHolder":
            this.serviceAgreementPolicyAddress.property =
              ui.PropertyAddressProperty;
            this.serviceAgreementPolicyAddress.organisation =
              ui.PropertyAddressBusinessName;
            this.serviceAgreementPolicyAddress.street =
              ui.PropertyAddressStreet;
            this.serviceAgreementPolicyAddress.locality =
              ui.PropertyAddressLocality;
            this.serviceAgreementPolicyAddress.town = ui.PropertyAddressCity;
            this.serviceAgreementPolicyAddress.postcode =
              ui.PropertyAddressPostcode;
            break;

          case "Customer":
            this.serviceAgreementCustomerAddress.property =
              ui.PropertyAddressProperty;
            this.serviceAgreementCustomerAddress.organisation =
              ui.PropertyAddressBusinessName;
            this.serviceAgreementCustomerAddress.street =
              ui.PropertyAddressStreet;
            this.serviceAgreementCustomerAddress.locality =
              ui.PropertyAddressLocality;
            this.serviceAgreementCustomerAddress.town = ui.PropertyAddressCity;
            this.serviceAgreementCustomerAddress.postcode =
              ui.PropertyAddressPostcode;
            break;

          default:
            break;
        }
        break;

      case "Contact":
        switch (scope) {
          case "Billing":
            ui.CarePlanBillingContactEmail = ui.PropertyContactEmail;
            ui.CarePlanBillingContactFirstName = ui.PropertyContactFirstName;
            ui.CarePlanBillingContactLastName = ui.PropertyContactLastName;
            ui.CarePlanBillingContactLandline = ui.PropertyContactLandline;
            ui.CarePlanBillingContactMobile = ui.PropertyContactMobile;
            ui.CarePlanBillingContactTitle =
              ui.PropertyContactTitle || undefined;
            break;

          case "Customer":
            this.serviceAgreementCustomer.emailAddress =
              ui.PropertyContactEmail;
            this.serviceAgreementCustomer.forename =
              ui.PropertyContactFirstName;
            this.serviceAgreementCustomer.surname = ui.PropertyContactLastName;
            this.serviceAgreementCustomer.telephoneLandline =
              ui.PropertyContactLandline;
            this.serviceAgreementCustomer.telephoneMobile =
              ui.PropertyContactMobile;
            this.serviceAgreementCustomer.title = ui.PropertyContactTitle ?? "";
            break;

          default:
            break;
        }
        break;

      default:
        break;
    }
  }

  /**
   * Retrieves address lines from a converted address object.
   *
   * Converts form fields related to the "CarePlanBillingAddress" scope into an
   * address object using AddressUtils, and extracts individual address lines.
   *
   * @returns An object containing address lines including line1, line2, line3,
   *          city, postcode, and county. Returns empty strings for any missing fields.
   */
  getAddressLines = () => {
    const scope =
      ui.CarePlanBillingAddressAsProperty === true
        ? "PropertyAddress"
        : "CarePlanBillingAddress";
    const addressObj = AddressUtils.convertAddressFormFieldsToAddress({
      scope,
      ui,
    })?.addressObj;
    return {
      addressLine1: addressObj?.line1 || "",
      addressLine2: addressObj?.line2 || "",
      addressLine3: addressObj?.line3 || "",
      city: addressObj?.postTown || "",
      postcode: addressObj?.postcode || "",
      county: addressObj?.county || "",
    };
  };

  getBillingCustomerData = (
    customerId?: number,
  ): RightioGoCardlessAPIWrapperModelsDtoCustomerRequestDto => {
    const { addressLine1, addressLine2, addressLine3, city, postcode, county } =
      this.getAddressLines();
    return {
      addressLine1,
      addressLine2,
      addressLine3,
      city,
      postcode,
      email: ui.CarePlanBillingContactEmail || "dd@rightio.co.uk",
      forename: ui.CarePlanBillingContactFirstName,
      surname: ui.CarePlanBillingContactLastName,
      county,
      metaData: customerId
        ? {
            ServiceAgreementCustomerId: `${customerId}`,
          }
        : undefined,
    };
  };

  async submit(ui: TemplateUIObject, userId: number, ddPayment = false) {
    // Prevent multiple submissions if one is already in progress
    if (this._isSubmitting) {
      logger.warn(namespace, "SUBMIT->Submission already in progress");
      return;
    }

    this._isSubmitting = true;
    state.SavingJob = true;

    // Generate an idempotency key for the submission
    if (!this._currentSubmissionKey) {
      this._currentSubmissionKey = this.generateIdempotencyKey();
    }

    logger.debug(
      namespace,
      "SUBMIT->Current submission key:",
      this._currentSubmissionKey,
    );
    logger.debug(namespace, "SUBMIT->Starting submit");

    const isCustomerContactSameAs = ui.CarePlanCustomerContactAsContact;
    const isCustomerAddressSameAs = ui.CarePlanCustomerAddressAsProperty;
    const isPolicyAddressSameAs = ui.CarePlanPlanDetailsAddressAsProperty;
    const isBillingAddressSameAs = ui.CarePlanBillingAddressAsProperty;
    const isBillingContactSameAs = ui.CarePlanBillingContactAsContact;

    if (isCustomerContactSameAs) {
      this.updatePersonalDetails(ui, "Customer", "Contact");
    }
    if (isCustomerAddressSameAs) {
      this.updatePersonalDetails(ui, "Customer", "Address");
    }
    if (isPolicyAddressSameAs) {
      this.updatePersonalDetails(ui, "PolicyHolder", "Address");
    }
    if (isBillingAddressSameAs) {
      this.updatePersonalDetails(ui, "Billing", "Address");
    }
    if (isBillingContactSameAs) {
      this.updatePersonalDetails(ui, "Billing", "Contact");
    }

    // check if we have valid service agreement offer
    const isServiceAgreementOfferValid =
      this.serviceAgreementOffer.serviceAgreementOfferId > 0;

    // Create customer first
    logger.debug(namespace, "SUBMIT->Creating customer");
    const customerId =
      await this.serviceAgreementCustomer.createCustomerInDatabase();
    if (!customerId) {
      dispatchSetSnackMsgError({
        ProblemMsg: "Customer creation failed!",
      });
      logger.error(namespace, "SUBMIT->Customer creation failed");
      state.SavingJob = false;
      this._isSubmitting = false;
      return;
    }

    // if direct debit is selected we have to process GoCardless
    if (ddPayment) {
      logger.debug(
        namespace,
        "SUBMIT->Creating GoCardless customer and bank account",
      );
      const customerRequest = this.getBillingCustomerData(customerId);
      const goCardlessBankAccount = await createGoCardlessBankAccount(
        customerId,
        customerRequest,
      );
      if (!goCardlessBankAccount || !goCardlessBankAccount.id) {
        dispatchSetSnackMsgError({
          ProblemMsg: "GoCardless bank account creation failed!",
        });
        logger.error(
          namespace,
          "SUBMIT->GoCardless bank account creation failed",
        );
        state.SavingJob = false;
        this._isSubmitting = false;
        return;
      }
      this.serviceAgreementPaymentDetails.goCardlessBankAccountId =
        goCardlessBankAccount.id;
    }

    //here we will dispatch api action and build object
    logger.debug(namespace, "SUBMIT->Building data object");
    const data: CCNewAPIDtoCreateAgreementDto =
      ObjectsOperations.typeSafeStructuredClone({
        addresses: {
          policyAddress: this.serviceAgreementPolicyAddress.toRequest(),
          customerAddress: this.serviceAgreementCustomerAddress.toRequest(),
          billingAddress: this.serviceAgreementBillingAddress.toRequest(),
          usePolicyAddressForAll: false,
        },
        paymentDetails: this.serviceAgreementPaymentDetails.toRequest(),
        offerParams: isServiceAgreementOfferValid
          ? this.serviceAgreementOffer.toRequest()
          : [],
        agreement: this.toRequest(userId),
      });

    // patch billing frequency in payment details
    logger.debug(namespace, "SUBMIT->Patching billing frequency");
    this.patchBilling(data);

    // const dataDeepCopy = ObjectsOperations.typeSafeStructuredCloneAll(data);
    dispatchUILoading();

    logger.debug(
      namespace,
      "SUBMIT->Calling apiV2.postApiAgreementsCreateNewAgreement",
    );
    try {
      const response = await apiV2.postApiAgreementsCreateNewAgreement(data);
      const ref = response && response.serviceAgreementReference;
      ui.CarePlanReference = ref ? ref : "";
      if (ref) {
        logger.debug(namespace, "SUBMIT->Calling getSAForJobId");
        await this.getSAForJobId(state.JobId);
        //After this service agreement needs to be cleared, form needs to disable service agreements/ possibly present REF number only of service agreement
        this.reset();
      }
    } catch (e) {
      logger.error(namespace, "submit", e);
      console.log(e);
      try {
        // There are 2 stages in plan creation.
        // The first creates the plan in the database and is where we get the reference from.
        // The second is where payment details are assigned and the welcome email is sent out.
        // It's possible that the agreement has been created but there was an issue on Stripe,
        // Go Cardless, or with sending out the welcome email.

        // To determine what to display to the user, we attempt to get the plan details.
        const response = await api.fetchServiceAgreementByJobId(state.JobId);
        const data = response?.[0];
        if (data) {
          // We found the care plan that was created, most likely it failed during the second stage.
          // In this case, carry on as normal but display a warning telling the user to inform
          // Care Club so they can check this plan.
          ui.CarePlanReference = data.serviceAgreementReference ?? "";

          // Get the service agreement attached to job
          await this.getSAForJobId(state.JobId);

          // Send the care club team an email detailing the reference and the job ID
          const issueCreatingPlanMsg = `<p>An issue occurred when creating a Care Plan on WBS:</p>
          <p><strong>Reference:</strong> ${ui.CarePlanReference}</p>
          <p><strong>Job ID:</strong> ${state.JobId}</p>
          <p>The plan itself was created, the issue would have most likely occurred in one of the following areas: </p>
          <ul>
          <li>Go Cardless Bank Subscription Set Up</li>
          <li>Stripe Subscription Set Up</li>
          <li>Sending Welcome Email/Letter</li>
          </ul>
          <p>Please investigate this care plan to avoid issues with payment.</p>`;

          await apiV2.postApiEmailSendEmail({
            toEmail: ["reachout@careclub.co.uk", "ellen.mccann@rightio.co.uk"],
            toUserName: ["Care Club", "Ellen McCann"],
            subject: `Issue on creating ${ui.CarePlanReference}`,
            message: issueCreatingPlanMsg,
            source: "WBS - Care Plan Creation",
          });

          //After this service agreement needs to be cleared, form needs to disable service agreements/ possibly present REF number only of service agreement
          this.reset();

          // Notify the user there was an issue
          dispatchSetSnackMsgWarn({
            ProblemMsg:
              "There was an issue creating this plan. Please inform Care Club so they can investigate.",
          });
        } else throw new Error("Could not obtain service agreement for JobID");
      } catch (e) {
        // We're unable to find a service agreement for this job, meaning the creation most likely failed at the first stage.
        // An error is displayed here asking the user to check what they've provided, refresh their browser and try again.
        logger.error(namespace, "submit", e);
        console.log(e);
        dispatchSetSnackMsgError({
          ProblemMsg:
            "An error occurred when saving the plan, please check with care club before proceeding further.",
        });
      }
    } finally {
      dispatchUILoaded();
      state.SavingJob = false;
      this._isSubmitting = false;
      this._currentSubmissionKey = null;
      logger.debug(namespace, "SUBMIT->Finished submit");
    }
  }

  private patchBilling(data: CCNewAPIDtoCreateAgreementDto) {
    if (
      data.paymentDetails?.serviceAgreementBillingFrequencyId &&
      data.paymentDetails?.serviceAgreementBillingFrequencyId > 3
    ) {
      data.paymentDetails.serviceAgreementBillingFrequencyId =
        this.serviceAgreementPaymentDetails.serviceAgreementBillingFrequencyId >
        3
          ? this.serviceAgreementOffer.serviceAgreementDurationId
          : this.serviceAgreementPaymentDetails
              .serviceAgreementBillingFrequencyId;
    }
  }

  toObject() {
    logger.debug(namespace, "toObject");
    return exportObject({
      _serviceAgreement: this.serviceAgreement.toObject(),
      _serviceAgreementBillingAddress:
        this.serviceAgreementBillingAddress.toObject(),
      _serviceAgreementCustomerAddress:
        this.serviceAgreementCustomerAddress.toObject(),
      _serviceAgreementPolicyAddress:
        this.serviceAgreementPolicyAddress.toObject(),
      _serviceAgreementPaymentDetails:
        this.serviceAgreementPaymentDetails.toObject(),
      _serviceAgreementCustomer: this.serviceAgreementCustomer.toObject(),
      _serviceAgreementOffer: this.serviceAgreementOffer.toObject(),
    });
  }

  objectAssign(target: any, source: any) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        // recursively call itself to copy child properties if value is instance of ServiceAgreementBody
        if (target[key] instanceof ServiceAgreementBody) {
          target[key] = new ServiceAgreementBody();
          Object.assign(
            target[key],
            ObjectsOperations.typeSafeStructuredClone(source[key]),
          );
          // recursively call itself to copy child properties if value is instance of Address
        } else if (target[key] instanceof ServiceAgreementAddress) {
          target[key] = new ServiceAgreementAddress();
          Object.assign(
            target[key],
            ObjectsOperations.typeSafeStructuredClone(source[key]),
          );
          // recursively call itself to copy child properties if value is instance of ServiceAgreementPaymentDetails
        } else if (target[key] instanceof ServiceAgreementPaymentDetails_v2) {
          target[key] = new ServiceAgreementPaymentDetails_v2();
          Object.assign(
            target[key],
            ObjectsOperations.typeSafeStructuredClone(source[key]),
          );
          // recursively call itself to copy child properties if value is instance of ServiceAgreementCustomer
        } else if (target[key] instanceof ServiceAgreementCustomer) {
          target[key] = new ServiceAgreementCustomer();
          Object.assign(
            target[key],
            ObjectsOperations.typeSafeStructuredClone(source[key]),
          );
          // recursively call itself to copy child properties if value is instance of OfferParams_v3
        } else if (target[key] instanceof OfferParams_v3) {
          target[key] = new OfferParams_v3();
          Object.assign(
            target[key],
            ObjectsOperations.typeSafeStructuredClone(source[key]),
          );
        } else {
          Object.assign(target[key], source[key]);
        }
      }
    }
  }

  private getSAForJobId = async (jobId: number) => {
    const response = await api.fetchServiceAgreementByJobId(jobId);
    const data = response?.[0];
    if (data) {
      // can be moved to middleware
      state.JobServiceAgreementId = data.serviceAgreementId;
      state.NotesCarePlan = "NEW RIGHTIO CARE CLUB CUSTOMER - PRIORITY JOB";
    }
  };
}

export type KeyOfSA = keyof ServiceAgreement_v2;

export type KeyOfSAAddress = keyof ServiceAgreementAddress;

export type KeyOfSACustomer = keyof ServiceAgreementCustomer;

export const serviceAgreement = new ServiceAgreement_v2();

declare global {
  interface Window {
    SA: any;
  }
}

window.SA = serviceAgreement;
